import { type ReactNode, createContext, useContext, useState } from 'react';
import { type ValidIcons, Icon } from '../Icon';
import { Spacer } from '../Spacer/Spacer';
import { Typography, type TypographyProps } from '../Typography/Typography';
import { getKDSClasses, merge } from '../util';

type SectionItem = {
  1: TextData | SectionData[2];
  2: TextData | SectionData[3];
  3: TextData;
};

type SectionData = {
  1: Pretty<Omit<SectionAt<1>, 'type'> & { icon?: ValidIcons }>;
  2: SectionAt<2>;
  3: SectionAt<3>;
};

type TrailingTextItem =
  | { type: 'text'; text: string }
  | { type: 'link'; text: string; href: string }
  | { type: 'space' };

type TextData = {
  type: 'text';
  label: string;
  icon?: ValidIcons;
  href?: string;
  separator?: 'colon' | 'space';
  trailingText?: TrailingTextItem[];
};

type SectionAt<N extends keyof SectionItem> = Pretty<{
  type: 'section';
  label: string;
  items: Array<SectionItem[N]>;
}>;

type SectionProps = {
  [K in keyof SectionData]: Pretty<Omit<SectionData[K], 'type'>>;
};

type ButtonHeaderProps = {
  children: ReactNode;
  onClick(): void;
};

type CollapseContainerProps = {
  children: ReactNode;
  isExpanded: boolean;
};

type SubsectionProps = {
  children?: ReactNode;
  hasChildren: boolean;
  label: string;
};

interface TextProps extends RequiredWithUndefined<Omit<TextData, 'type'>> {
  indented?: boolean;
}

type TextSeparator = Required<TextData>['separator'];

export type InformationTreeProps = {
  sections: Array<Pretty<SectionData[1]>>;
};

const LevelContext = createContext(1);

const Separators: Record<TextSeparator, string> = {
  colon: ':\u00A0',
  space: '\u00A0',
};

const truncateProps = (shouldTruncate = true): Partial<TypographyProps> => {
  if (shouldTruncate) {
    return {
      className: 'min-w-0',
      modalCompatibility: true,
      truncate: true,
    };
  }

  return {};
};

const ButtonHeader = (props: ButtonHeaderProps) => {
  const { children, onClick } = props;

  return (
    <button
      type="button"
      onClick={onClick}
      className="appearance-none bg-transparent border-0 cursor-pointer flex items-center w-max pb-[5px]"
    >
      {children}
    </button>
  );
};

const CollapseContainer = (props: CollapseContainerProps) => {
  const { children, isExpanded } = props;

  return (
    <div
      className={merge(
        'grid transition-[grid-template-rows] duration-300',
        isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      )}
    >
      <div className="overflow-hidden flex flex-col">
        <Spacer size={10} mode="horizontal" />
        <div className="flex flex-col gap-10">{children}</div>
      </div>
    </div>
  );
};

const Subsection = (props: SubsectionProps) => {
  const { children, hasChildren, label } = props;
  const [isExpanded, setIsExpanded] = useState(true);
  const level = useContext(LevelContext) + 1;

  return (
    <LevelContext.Provider value={level}>
      <div className="flex flex-col ml-[25px]">
        <ButtonHeader onClick={() => setIsExpanded((x) => !x)}>
          <Typography
            size={level === 3 ? 'sm' : 'md'}
            weight="semibold"
            color="font-primary"
          >
            {label}
          </Typography>
          <Spacer size={10} />
          <Icon
            icon="custom-chevron-down"
            size={level === 3 ? 'xs' : 'sm'}
            color="font-primary"
            tooltipEnabled={false}
            className={merge(
              'transition-transform duration-300 ease-in-out',
              isExpanded ? 'rotate-0' : '-rotate-90'
            )}
          />
        </ButtonHeader>
        {hasChildren && (
          <CollapseContainer isExpanded={isExpanded}>
            {children}
          </CollapseContainer>
        )}
      </div>
    </LevelContext.Provider>
  );
};

const Section1 = (props: SectionProps[1]) => {
  const { label, icon, items = [] } = props;
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <LevelContext.Provider value={1}>
      <div className="flex flex-col mb-[15px] last-of-type:mb-0">
        <ButtonHeader onClick={() => setIsExpanded((x) => !x)}>
          {icon && (
            <div className="min-w-[25px]">
              <Icon icon={icon} size="lg" />
            </div>
          )}
          <Typography size="lg" weight="medium" color="font-primary">
            {label}
          </Typography>
          <Spacer size={10} />
          <Icon
            size="md"
            icon="custom-chevron-down"
            tooltipEnabled={false}
            className={merge(
              'transition-transform duration-300 ease-in-out',
              isExpanded ? 'rotate-0' : '-rotate-90'
            )}
          />
        </ButtonHeader>
        {items.length > 0 && (
          <CollapseContainer isExpanded={isExpanded}>
            {items.map((item) => {
              if (item.type === 'section') {
                return (
                  <Section2
                    key={item.label}
                    label={item.label}
                    items={item.items}
                  />
                );
              }

              if (item.type === 'text') {
                return (
                  <Text
                    key={item.label}
                    icon={item.icon}
                    label={item.label}
                    href={item.href}
                    separator={item.separator}
                    trailingText={item.trailingText}
                  />
                );
              }
            })}
          </CollapseContainer>
        )}
      </div>
    </LevelContext.Provider>
  );
};

const Section2 = (props: SectionProps[2]) => {
  const { label, items = [] } = props;

  return (
    <Subsection label={label} hasChildren={items.length > 0}>
      {items.map((item) => {
        if (item.type === 'section') {
          return (
            <Section3 key={item.label} label={item.label} items={item.items} />
          );
        }

        if (item.type === 'text') {
          return (
            <Text
              key={item.label}
              icon={item.icon}
              label={item.label}
              href={item.href}
              separator={item.separator}
              trailingText={item.trailingText}
            />
          );
        }
      })}
    </Subsection>
  );
};

const Section3 = (props: SectionProps[3]) => {
  const { label, items = [] } = props;
  const indented = items.every((item) => !item.icon);

  return (
    <Subsection label={label} hasChildren={items.length > 0}>
      {items.map((item) => (
        <Text
          key={item.label}
          label={item.label}
          href={item.href}
          icon={item.icon}
          indented={indented}
          separator={item.separator}
          trailingText={item.trailingText}
        />
      ))}
    </Subsection>
  );
};

const TextItem = (props: { item: TrailingTextItem; truncate?: boolean }) => {
  const { item, truncate = false } = props;

  if (item.type === 'link') {
    return (
      <Typography
        {...truncateProps(truncate)}
        variant="link"
        size="md"
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {item.text}
      </Typography>
    );
  }

  if (item.type === 'space') {
    return <Typography size="md">&nbsp;</Typography>;
  }

  return (
    <Typography {...truncateProps(truncate)} size="md" color="font-primary">
      {item.text}
    </Typography>
  );
};

const Text = (props: TextProps) => {
  const {
    href,
    icon,
    label,
    indented = true,
    trailingText = [],
    separator = 'space',
  } = props;

  const hasTrailingText = trailingText.length > 0;

  return (
    <div
      className={merge(
        'flex items-center gap-10 pb-[5px]',
        indented && 'ml-[25px]'
      )}
    >
      {icon && <Icon icon={icon} size="md" />}
      <span className="flex min-w-0">
        {href ? (
          <Typography
            {...truncateProps(!hasTrailingText)}
            variant="link"
            size="md"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {label}
          </Typography>
        ) : (
          <Typography
            {...truncateProps(!hasTrailingText)}
            size="md"
            color="font-primary"
          >
            {label}
          </Typography>
        )}
        {hasTrailingText && (
          <Typography size="md" color="font-primary">
            {Separators[separator]}
          </Typography>
        )}
        {trailingText.length === 1 && (
          <TextItem truncate item={trailingText[0]} />
        )}
        {trailingText.length > 1 &&
          trailingText.map((item, i) => (
            <TextItem
              key={i}
              item={item}
              truncate={i === trailingText.length - 1}
            />
          ))}
      </span>
    </div>
  );
};

export const InformationTree = (props: InformationTreeProps) => {
  return (
    <div className={getKDSClasses('information-tree')}>
      {props.sections.map((section) => (
        <Section1
          key={section.label}
          label={section.label}
          icon={section.icon}
          items={section.items}
        />
      ))}
    </div>
  );
};
